/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTeamStore } from '@/stores/team';
import DateUtility from '@/utilities/dates';
import type { ActionOption, ActivityItem } from './useActivityTransformer';
import { HumanizString } from '@/utilities/humaniz_string';
import { useMeetingUsher } from '@/composables/useMeetingUsher';

export const useTransformEvent = () => {
  const teamStore = useTeamStore();
  const meetingUsher = useMeetingUsher();

  const transformEvent = (contactEvent: any, contact?: any): ActivityItem => {
    const MeetingDashboardAction = {
      onClick: () => meetingUsher.navigateToMeeting(contactEvent),
    };

    const meetingTime =
      DateUtility.formatDate(contactEvent.start_time, 'MMMM dd yyyy, h:mm a') +
      ' to ' +
      DateUtility.formatDate(contactEvent.end_time, 'h:mm a');

    const buildButtons = () => {
      const buttons: ActionOption[] = [];

      const pastMeetingTitle = () => {
        if (contactEvent.event_type === 'pipeline_review') {
          return 'Pipeline Review';
        } else {
          return contactEvent.scorecard ? 'View scorecard' : 'Submit scorecard';
        }
      };

      const pastMeetingDashboardButton = {
        title: pastMeetingTitle(),
        action: MeetingDashboardAction,
      };

      if (meetingUsher.isPastEvent(contactEvent)) {
        buttons.push(pastMeetingDashboardButton);
      }

      return buttons;
    };

    const buildDropdownOptions = () => {
      if (meetingUsher.isPastEvent(contactEvent)) return;

      const menuItems: ActionOption[] = [];

      const meetingDashboardItem: ActionOption = {
        title: meetingUsher.actionTitle(contactEvent),
        icon: 'clipboard',
        action: MeetingDashboardAction,
      };

      const rescheduleItem: ActionOption = {
        title: 'Reschedule',
        icon: 'calendar',
        action: { href: teamStore.getScheduleFollowupLink(contactEvent.contact_id, 'contact_reschedule') ?? undefined },
      };

      const deleteItem: ActionOption = {
        title: `Delete ${contactEvent.name}`,
        icon: 'trash',
        action: { emitId: 'delete-interview-clicked' },
      };

      if (contactEvent.status !== 'canceled') {
        menuItems.push(meetingDashboardItem);
      }
      menuItems.push(rescheduleItem);
      if (contactEvent.status === 'active') {
        menuItems.push(deleteItem);
      }

      return menuItems;
    };

    const resolveActor = () => {
      if (contactEvent.scheduled_by === 'contact' && contact) return contact.name?.trim() || (contact.email ?? '');
      if (contactEvent.scheduler_user)
        return contactEvent.scheduler_user.name?.trim() || (contactEvent.scheduler_user.email ?? '');
      return contactEvent.user.name?.trim() || (contactEvent.user.email ?? '');
    };

    const interviewer = contactEvent.user.name?.trim() || (contactEvent.user.email ?? '');
    const interviewee = contact?.name ?? 'Candidate';

    const buildContent = () => [
      { label: 'Status', richText: HumanizString.capitalize(contactEvent.status) },
      { label: contactEvent.name, richText: meetingTime },
      { label: 'Interviewer', richText: interviewer },
      { label: 'Interviewee', richText: interviewee },
    ];

    return {
      id: contactEvent.id,
      categories: ['timeline'],
      icon: 'calendar-days',
      verb: 'scheduled a meeting',
      typeLabel: 'Meeting',
      displayTimestamp: contactEvent.start_time,
      sortTimestamp: contactEvent.end_time,
      actor: resolveActor(),
      label: contactEvent.name,
      content: buildContent(),
      buttons: buildButtons(),
      dropdownOptions: buildDropdownOptions(),
    };
  };

  return { transformEvent };
};
